/**
 * Tree-shakeable lodash chain utility.
 *
 * Instead of `import _ from 'lodash'` (pulls entire library), import from here:
 *   import { _ } from '@omni/utility/lodash-chain';
 *
 * Only the methods registered below are available on the chain wrapper.
 * To add a new method: import it from 'lodash-es/<method>' and add it to
 * the appropriate section (chainable or unwrapped).
 */
import lodash from 'lodash-es/wrapperLodash';
import mixin from 'lodash-es/mixin';
import wrapperValue from 'lodash-es/wrapperValue';
import wrapperChain from 'lodash-es/wrapperChain';

// Chainable methods — return wrapped values in chain sequences
import filter from 'lodash-es/filter';
import flatMap from 'lodash-es/flatMap';
import fromPairs from 'lodash-es/fromPairs';
import groupBy from 'lodash-es/groupBy';
import map from 'lodash-es/map';
import orderBy from 'lodash-es/orderBy';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import concat from 'lodash-es/concat';
import uniqBy from 'lodash-es/uniqBy';
// Unwrapped methods — return primitive/unwrapped values, end the chain
import find from 'lodash-es/find';
import some from 'lodash-es/some';
import sum from 'lodash-es/sum';
import reduce from 'lodash-es/reduce';
import type { LoDashStatic } from 'lodash';
import type _ from 'lodash';

function sort<T>(array: T[], compareFn?: (a: T, b: T) => number): T[] {
  if (!Array.isArray(array)) return array as any;

  // avoid mutation (important)
  const copy = [...array];

  return compareFn ? copy.sort(compareFn) : copy.sort();
}

const chain: LoDashStatic = function (input) {
  // Register chainable methods
  const chainable = {
    filter,
    flatMap,
    fromPairs,
    groupBy,
    map,
    orderBy,
    sort,
    sortBy,
    uniq,
    concat,
    uniqBy
  };
  Object.assign(lodash, chainable);
  mixin(lodash, chainable);

  // Register unwrapped methods. `reduce` belongs here (not in chainable)
  // because it's a terminator — it returns the accumulator, not a collection.
  // Registering it with chain:false makes implicit-chain calls auto-unwrap
  // (`_(arr).map(...).reduce(fn, init)` returns the accumulator), matching
  // native lodash behaviour. Explicit `_.chain(arr).reduce(...).value()`
  // still works because the chain wrapper queues the action and resolves
  // it on .value().
  const unwrapped = { find, some, sum, reduce };
  Object.assign(lodash, unwrapped);
  mixin(lodash, unwrapped, { chain: false });

  // Wire up chain sequence termination
  lodash.prototype.chain = wrapperChain;
  lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

  return lodash(input);
} as any;
export { chain as _ };

